import React from 'react';
import { observer } from '../../shared/observer';
import { debounce } from '../../shared/debounce';

// Глобальный диалог «Добавить адрес» — повторяет client_app/Places/AddAddress.
//
// Логика шага `picker`:
//  • Карта Яндекс на весь верх диалога, центрирована на city_branch.
//  • В центре карты — фиксированная булавка (DOM-overlay).
//  • Сверху — поле автокомплита, выбор саджеста двигает карту.
//  • Кнопка «Использовать эту точку»: если есть lastSuggestion — берём
//    его координаты (setAddressByLastSuggest), иначе reverseGeocode по
//    центру карты. Если district_id найден — переходим на details;
//    если нет — показываем ошибку «адрес вне зоны доставки».
//
// Логика шага `details`:
//  • Карта-превью + форма (Город/Улица/Дом/...) + «Назад» / «Сохранить».
class AddAddressDialogView extends React.Component {
  state = {
    step: 'picker', // 'picker' | 'details'
    query: '',
    suggestions: [],
    searching: false,
    saving: false,
    processing: false, // во время reverseGeocode / setAddressByLastSuggest
    error: null,
  };

  componentWillMount() {
    this._suggestDebounced = debounce((q) => this.runSuggest(q), 400);
    this.setMapNode = (n) => { this._mapNode = n; };
  }

  componentWillReceiveProps(next) {
    if (next.address.addDialogOpen && !this.props.address.addDialogOpen) {
      // В режиме редактирования сразу шаг details — основной адрес
      // (city/street/dom) и координаты уже подтянуты в store через openEditDialog.
      const editing = !!next.address.editingAddressId;
      this.setState({
        step: editing ? 'details' : 'picker',
        query: '', suggestions: [], searching: false,
        saving: false, processing: false, error: null,
      });
      // Карту построим в componentDidUpdate, когда DOM появится.
    }
    if (!next.address.addDialogOpen && this.props.address.addDialogOpen) {
      this.destroyMap();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const justOpened = this.props.address.addDialogOpen && !prevProps.address.addDialogOpen;
    const prevStep = prevProps.address.editingAddressId ? 'details' : prevState.step;
    const curStep = this.currentStep();
    const stepChanged = prevStep !== curStep;
    if (justOpened || stepChanged) {
      this.destroyMap();
      this.ensureMap();
    } else if (this.props.address.addDialogOpen && !this._map) {
      this.ensureMap();
    }
  }

  componentWillUnmount() { this.destroyMap(); }

  destroyMap() {
    if (this._map) {
      try { this._map.destroy(); } catch (e) { /* noop */ }
      this._map = null;
    }
  }

  // ---------- Карта
  ensureMap() {
    if (this._mapPending || this._map) return;
    if (!window.ymaps || !window.ymaps.ready) return;
    this._mapPending = true;
    // setTimeout даёт DOM-layout время посчитать размеры контейнера
    // после открытия диалога (иначе ymaps создаст 0×0 карту).
    setTimeout(() => {
      this._mapPending = false;
      const node = this._mapNode;
      if (!node || this._map) return;
      if (node.offsetWidth === 0 || node.offsetHeight === 0) return;
      const loc = this.getInitialCenter();
      if (!loc) return;
      window.ymaps.ready(() => {
        if (!this._mapNode || this._map) return;
        const map = new window.ymaps.Map(this._mapNode, {
          center: loc,
          zoom: 16,
          controls: ['zoomControl'],
        });
        map.behaviors.disable('scrollZoom');
        // Зоны доставки (1:1 с client_app/Places/AddAddress/Map): каждая
        // zone.coords — массив [lat, lng] пар, рисуем как ymaps.Polygon.
        const zones = this.props.auth.deliveryZones || [];
        let drawn = 0;
        zones.forEach((z) => {
          const ring = (z.coords || [])
            .filter((c) => Array.isArray(c) && c.length >= 2 && c[0] != null && c[1] != null)
            .map((c) => [Number(c[0]), Number(c[1])]);
          if (ring.length < 3) return;
          const poly = new window.ymaps.Polygon([ring], {}, {
            fillColor: 'rgba(100, 100, 200, 0.25)',
            strokeColor: 'rgb(50, 50, 100)',
            strokeWidth: 2,
            opacity: 1,
            zIndex: 9999,
          });
          map.geoObjects.add(poly);
          drawn += 1;
        });
        // На picker'e: подгоняем карту так, чтобы зоны попали в кадр.
        // Если bbox слишком большой (зоны в разных городах) — не отдаляемся,
        // оставляем центр на city_branch с городским zoom.
        if (drawn > 0 && this.currentStep() === 'picker' && !this.props.address.inputAddress.location.lat) {
          try {
            const bounds = map.geoObjects.getBounds(); // [[lat1, lng1], [lat2, lng2]]
            if (bounds && bounds[0] && bounds[1]) {
              const latSpan = Math.abs(bounds[1][0] - bounds[0][0]);
              const lngSpan = Math.abs(bounds[1][1] - bounds[0][1]);
              // ~0.5° по широте/долготе ≈ городской масштаб; больше — несколько городов
              if (latSpan < 0.5 && lngSpan < 0.7) {
                map.setBounds(bounds, { checkZoomRange: true, zoomMargin: 24 });
              } else {
                map.setCenter(loc, 12, { duration: 0 });
              }
            }
          } catch (e) { /* noop */ }
        }
        if (this.currentStep() === 'details') {
          const inp = this.props.address.inputAddress.location || {};
          if (inp.lat && inp.lng) {
            map.geoObjects.add(new window.ymaps.Placemark([Number(inp.lat), Number(inp.lng)], {}, {
              preset: 'islands#redDotIcon',
            }));
            // На превью-карте сразу зум на адресе, не подгоняем bbox по зонам.
            map.setCenter([Number(inp.lat), Number(inp.lng)], 17, { duration: 0 });
          }
        }
        this._map = map;
      });
    }, 80);
  }

  getInitialCenter() {
    const inp = this.props.address.inputAddress.location || {};
    if (inp.lat && inp.lng) return [Number(inp.lat), Number(inp.lng)];
    const cb = this.props.auth.cityBranch;
    if (cb && cb.location && cb.location.lat && cb.location.lng) {
      return [Number(cb.location.lat), Number(cb.location.lng)];
    }
    return null;
  }

  getMapCenter() {
    if (!this._map) return null;
    try { return this._map.getCenter(); } catch (e) { return null; }
  }

  // ---------- Автокомплит
  setQuery = (e) => {
    const q = e.target.value;
    this.setState({ query: q, error: null });
    if (q.length >= 3) {
      this.setState({ searching: true });
      this._suggestDebounced(q);
    } else {
      // Отменяем запланированный debounce и инвалидируем in-flight запрос,
      // чтобы устаревший ответ не вернул «Ищем…»/чужие подсказки.
      this._suggestDebounced.cancel();
      this._suggestToken = (this._suggestToken || 0) + 1;
      this.setState({ suggestions: [], searching: false });
    }
  };

  runSuggest = async (q) => {
    if (q !== this.state.query) return; // за время debounce запрос устарел
    const token = (this._suggestToken || 0) + 1;
    this._suggestToken = token;
    try {
      const res = await this.props.address.autocomplete(q);
      if (token !== this._suggestToken || q !== this.state.query) return; // устаревший ответ
      this.setState({ suggestions: (res && res.items) || [], searching: false });
    } catch (e) {
      if (token !== this._suggestToken || q !== this.state.query) return;
      this.setState({ suggestions: [], searching: false, error: e.message || 'Не удалось получить подсказки' });
    }
  };

  pickSuggestion = (s) => () => {
    this.props.address.setLastSuggestion(s);
    // Отменяем запланированный debounce и инвалидируем in-flight запрос —
    // иначе ранний return в runSuggest оставит searching: true навсегда.
    this._suggestDebounced.cancel();
    this._suggestToken = (this._suggestToken || 0) + 1;
    this.setState({ suggestions: [], query: s.title || this.state.query, searching: false, error: null });
    // двигаем карту на координаты саджеста
    if (this._map && s.lat && s.lng) {
      try {
        this._map.setCenter([Number(s.lat), Number(s.lng)], 17, { duration: 200 });
      } catch (e) { /* noop */ }
    }
  };

  // ---------- Использовать эту точку (центр карты)
  usePoint = async () => {
    this.setState({ processing: true, error: null });
    try {
      if (this.props.address.lastSuggestion) {
        await this.props.address.setAddressByLastSuggest();
      } else {
        const c = this.getMapCenter();
        if (!c) {
          this.setState({ processing: false, error: 'Карта не готова, попробуйте ещё раз' });
          return;
        }
        await this.props.address.reverseGeocode(c[0], c[1]);
      }
      if (!this.props.address.deliveryAvailable) {
        this.setState({ processing: false, error: 'Адрес вне зоны доставки' });
        return;
      }
      this.setState({ processing: false, step: 'details', suggestions: [] });
    } catch (e) {
      this.setState({ processing: false, error: e.message || 'Не удалось определить адрес' });
    }
  };

  // ---------- Details
  setField = (k) => (e) => this.props.address.setInputAddress({ [k]: e.target.value });

  back = () => {
    if (this.state.saving) return; // не уходим с шага, пока адрес сохраняется
    this.setState({ step: 'picker', error: null });
  };

  close = () => {
    if (this.state.saving) return; // не даём закрыть диалог во время сохранения
    this.forceClose();
  };

  forceClose = () => {
    this.destroyMap();
    this.props.address.closeAddDialog();
  };

  save = async () => {
    const a = this.props.address.inputAddress;
    const editingId = this.props.address.editingAddressId;
    if (!editingId && (!a.city || !a.street || !a.dom)) {
      this.setState({ error: 'Заполните город, улицу и дом' });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      if (editingId) {
        // Редактируем только «доп.» поля адреса (как в моб. EditAddress):
        // основная привязка (city/street/dom/location) остаётся прежней.
        await this.props.address.updateAddress(editingId, {
          kv: a.kv, korp: a.korp, entrance: a.entrance, floor: a.floor,
          client_comment: a.client_comment,
        });
      } else {
        const res = await this.props.address.createAddress(a);
        // Ошибку валидации сервер отдаёт с HTTP 200 ({status: 'error', errors}):
        // без этой проверки диалог закрывался, будто адрес сохранён, и клиент
        // уходил к оплате без адреса
        if (!res || !res.address) {
          const errors = (res && res.errors) || {};
          this.setState({
            saving: false,
            error: errors.district_id
              ? 'Адрес не найден в зоне доставки — выберите его из подсказок или отметьте на карте'
              : 'Не удалось сохранить адрес',
          });
          return;
        }
      }
      this.setState({ saving: false });
      this.forceClose();
    } catch (e) {
      this.setState({ saving: false, error: e.message || 'Не удалось сохранить адрес' });
    }
  };

  // ---------- Render
  renderPicker() {
    const { query, suggestions, searching, processing, error } = this.state;
    return (
      <div className="ng-add-address__picker">
        <div className="ng-add-address__search">
          <input
            className="input__text"
            type="text"
            placeholder="Поиск по адресу или передвиньте карту"
            value={query}
            onChange={this.setQuery}
            autoFocus
          />
          {searching && <div className="ng-checkout__hint">Ищем…</div>}
          {suggestions.length > 0 && (
            <ul className="ng-suggestions">
              {suggestions.map((s, i) => (
                <li key={`${s.lat}-${s.lng}-${i}`} onClick={this.pickSuggestion(s)}>{s.title}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="ng-add-address__map-wrapper">
          <div ref={this.setMapNode} className="ng-add-address__map" />
          <div className="ng-add-address__pin" aria-hidden="true">📍</div>
        </div>

        {error && <div className="order-message">{error}</div>}

        <div className="ng-add-address__actions">
          <button type="button" className="btn btn--block" onClick={this.usePoint} disabled={processing}>
            {processing ? 'Определяем…' : 'Использовать эту точку'}
          </button>
        </div>
      </div>
    );
  }

  renderDetails() {
    const a = this.props.address.inputAddress;
    const editing = !!this.props.address.editingAddressId;
    return (
      <div className="ng-add-address__details">
        <div ref={this.setMapNode} className="ng-add-address__map ng-add-address__map--preview" />

        {editing ? (
          // В режиме редактирования city/street/dom — read-only заголовок,
          // как в client_app/Places/EditAddress (нельзя сменить
          // привязку адреса к district, только доп.поля).
          <div className="ng-add-address__readonly">
            <strong>{[a.city, a.street, a.dom].filter(Boolean).join(', ')}</strong>
          </div>
        ) : (
          <div className="ng-add-address__form">
            <div className="ng-add-address__row">
              <div className="input"><label>Город</label>
                <input className="input__text" type="text" value={a.city || ''} onChange={this.setField('city')} />
              </div>
              <div className="input"><label>Улица</label>
                <input className="input__text" type="text" value={a.street || ''} onChange={this.setField('street')} />
              </div>
            </div>
            <div className="ng-add-address__row">
              <div className="input"><label>Дом</label>
                <input className="input__text" type="text" value={a.dom || ''} onChange={this.setField('dom')} />
              </div>
            </div>
          </div>
        )}

        <div className="ng-add-address__form">
          <div className="ng-add-address__row">
            <div className="input"><label>Корпус</label>
              <input className="input__text" type="text" value={a.korp || ''} onChange={this.setField('korp')} />
            </div>
            <div className="input"><label>Квартира</label>
              <input className="input__text" type="text" value={a.kv || ''} onChange={this.setField('kv')} />
            </div>
          </div>
          <div className="ng-add-address__row">
            <div className="input"><label>Подъезд</label>
              <input className="input__text" type="text" value={a.entrance || ''} onChange={this.setField('entrance')} />
            </div>
            <div className="input"><label>Этаж</label>
              <input className="input__text" type="text" value={a.floor || ''} onChange={this.setField('floor')} />
            </div>
          </div>
          <div className="input">
            <label>Комментарий курьеру</label>
            <textarea className="input__text" rows={2} value={a.client_comment || ''} onChange={this.setField('client_comment')} />
          </div>
        </div>

        {this.state.error && <div className="order-message">{this.state.error}</div>}

        <div className="ng-add-address__actions">
          {!editing && (
            <button type="button" className="btn btn--ghost" onClick={this.back} disabled={this.state.saving}>Назад</button>
          )}
          {editing && (
            <button type="button" className="btn btn--ghost" onClick={this.close} disabled={this.state.saving}>Отмена</button>
          )}
          <button type="button" className={'btn' + (this.state.saving ? ' is-busy' : '')} onClick={this.save} disabled={this.state.saving}>
            {this.state.saving ? 'Сохраняем…' : (editing ? 'Сохранить изменения' : 'Сохранить адрес')}
          </button>
        </div>
      </div>
    );
  }

  // Derive шага: в режиме редактирования всегда показываем details,
  // независимо от state.step (которое не успевает обновиться при first mount).
  currentStep() {
    if (this.props.address.editingAddressId) return 'details';
    return this.state.step;
  }

  render() {
    if (!this.props.address.addDialogOpen) return null;
    const step = this.currentStep();
    return (
      <div className="dialog">
        <div className="dialog__bg" onClick={this.close} />
        <div className="dialog__content dialog__content--wide">
          <div
            className={'dialog__close' + (this.state.saving ? ' is-disabled' : '')}
            aria-disabled={this.state.saving ? 'true' : null}
            onClick={this.close}
          />
          <h2 className="ng-add-address__title">
            {this.props.address.editingAddressId
              ? 'Изменить адрес'
              : (step === 'picker' ? 'Поиск адреса' : 'Уточните адрес')}
          </h2>
          {step === 'picker' ? this.renderPicker() : this.renderDetails()}
        </div>
      </div>
    );
  }
}

export const AddAddressDialog = observer(AddAddressDialogView, ['addressStore', 'authStore']);



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/components/Address/AddAddressDialog.jsx